<template lang="pug">
section
    WSearch(
        v-if="displaySearch"
        :displaySearchFilters="true"
        :searchFilters="searchFilters"
        @launchSearch="launchSearch"
        @updateSearchFilter="updateSearchFilter"
        :icon="searchIcon"
        :searchPlaceHolder="searchPlaceHolder"
    )

    WFilters(
        v-if="displayFilters"
        :filters="filters"
        :dateFilters="dateFilters"
        :filterDateOperators="filterDateOperators"
        :displayTo="displayTo"
        :showReset="true"
        :showPartnersFilter="showPartnersFilter"
        @updateFilter="updateFilter"
        @resetFilters="resetFilters"
        @updateDateOperator="updateDateOperator"
        @updateDate="updateDate"
        @filtersBlockDisplayed="filtersBlockOpen"
        @updatePartners="updatePartners"
    )

    WDataTable(
        :config="config"
        :mainData="paginatedData"
        :loadMain="loadData"
        :opened="tableData.opened"
        :rowClickFunc="rowClick"
        :totalItems="totalItems"
        :defaultSort="sorting"
        :defaultCurrentPage="pagination.currentPage"
        :itemsPerPageList="itemsPerPageList"
        :selectedParentId="selectedRowId"
        :loadChild="loadChildData"
        :children="children"
        :disabledRowIds="disabledRowIdsArray"
        :filtersBlockDisplayed="displayFiltersStatus"
    )

</template>

<script>
import { cloneDeep, debounce, findIndex, get, includes, isEmpty, isEqual, isNil, nth, size } from 'lodash'

import WDataTable from '@/components/shared/table/W-Data-Table'
import WFilters from '@/components/shared/filters/Filters'
import WSearch from '@/components/shared/search/Search'

import { dataToPaginateItems, dataToSortedItems, dataToSearchedItems, dataToFilteredItems } from '@/utils/helpers'
import { FILTERING_DEFAULT_DATES, ITEMS_PER_PAGE_LIST } from '@/utils/constants'

export default {
    name: 'Table',
    props: {
        fullData: { type: Array, required: true },
        tableData: { type: Object, required: true },
        children: { type: Object, required: false, default: () => ({}) },
        selectRow: { type: Function, required: true },
        setExpandedRows: { type: Function, required: false, default: () => ({}) },
        selectedRowId: { type: [Number, String], required: false, default: null },
        setActiveTab: { type: Function, required: false, default: () => ({}) },
        defaultTab: { type: String, required: false, default: '' },
        selectedData: { type: Number, required: false, default: null },
        displaySearch: { type: Boolean, required: true },
        searchFilters: { type: Array, required: false, default: () => [] },
        searchPlaceHolder: { type: String, required: false, default: '' },
        displayFilters: { type: Boolean, required: true },
        filtersData: { type: Object, required: false, default: () => ({}) },
        filterDatesKeys: { type: Array, required: false, default: () => [] },
        dateFilters: { type: Array, required: false, default: () => [] },
        defaultDates: { type: Object, required: false, default: () => ({}) },
        filterDateOperators: { type: Array, required: false, default: () => [] },
        audienceActions: { type: Object, required: false, default: () => ({}) },
        audienceInfos: { type: Object, required: false, default: () => ({}) },
        disabledRowIds: { type: Array, required: false, default: () => [] },
        showPartnersFilter: { type: Boolean, required: false, default: false }
    },

    components: { WDataTable, WFilters, WSearch },

    mounted() {
        if (this.isAudiences) {
            if (this.audienceInfos.isAudienceCcloneTargeted) {
                this.setCurrentPageToTargetSelectedAudience()
            }
            this.audienceActions.retrieveVolumes(this.paginatedData)
        }
    },

    activated() {
        if (this.isAudiences) {
            this.audienceActions.retrieveVolumes(this.paginatedData)
        }
    },

    watch: {
        audienceInfos: {
            handler: function(newValue) {
                if (newValue) {
                    if (newValue.selectedTagLabel !== null) {
                        this.searchFilter = 'tag'
                        this.launchSearch(newValue.selectedTagLabel)
                    } else if (newValue.selectedTagLabel === null) {
                        this.searchFilter = ''
                        this.isSearching = false
                    }
                    if (newValue.isAudienceCcloneTargeted) {
                        this.setCurrentPageToTargetSelectedAudience()
                    }
                }
            },
            deep: true
        },

        paginatedDataIds: {
            handler: function(newValue, oldValue) {
                if (!isEqual(newValue, oldValue)) {
                    if (this.isAudiences && !isEmpty(newValue)) {
                        this.debounceRetrieveVolumes()
                    }
                    //reset row selection if selected row is not visible by user
                    if (!isNil(this.selectedRowId) && newValue.indexOf(this.selectedRowId) === -1) {
                        this.selectRow({ rowId: null })
                        if (!isNil(this.defaultTab)) {
                            this.setActiveTab({ tab: this.defaultTab })
                        }
                    }
                }
            }
        },
        filteredData: {
            handler: function(newValue, oldValue) {
                if (this.isAudiences && newValue !== oldValue && !isNil(newValue)) {
                    this.audienceActions.updateFilteredData(newValue)
                }
            },
            deep: true
        },
        isSearching: {
            handler: function(newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.pagination.currentPage = 1
                }
            }
        },
        searchValue: {
            handler: function(newValue) {
                if (this.audienceInfos) {
                    if (newValue !== this.audienceInfos.selectedTagLabel && !isNil(this.audienceInfos.selectedTagLabel)) {
                        // in case there was a tag selected (by click) in the Table, reset it
                        this.audienceActions.resetTagSearch()
                    }
                }
            }
        },
        selectedData: {
            handler: function(newValue, oldValue) {
                if (!isNil(newValue) && !isEqual(newValue, oldValue) && this.pagination.currentPage !== 1) {
                    this.pagination.currentPage = 1
                }
            }
        },
        filterTargets: {
            handler: function() {
                this.pagination.currentPage = 1
            },
            deep: true
        },
        filterCreatedDate: {
            handler: function(newValue) {
                if (!isNil(get(newValue, 'from', null))) {
                    this.pagination.currentPage = 1
                }
            },
            deep: true
        },
        filterStartDate: {
            handler: function(newValue) {
                if (!isNil(get(newValue, 'from', null))) {
                    this.pagination.currentPage = 1
                }
            },
            deep: true
        },
        filterEndDate: {
            handler: function(newValue) {
                if (!isNil(get(newValue, 'from', null))) {
                    this.pagination.currentPage = 1
                }
            },
            deep: true
        },
        disabledRowIds: {
            handler: function(newValue) {
                // this is used only for the first time an account uses WAMfactory
                this.disabledRowIdsArray = newValue === undefined ? [] : newValue
            },
            deep: true
        }
    },

    data() {
        return {
            isAudiences: includes(this.$route.path, 'monitoring'),
            // table
            config: this.tableData.config,
            pagination: this.tableData.pagination,
            sorting: this.tableData.sorting,
            itemsPerPageList: ITEMS_PER_PAGE_LIST,

            // search
            searchIcon: { name: 'close', size: 'small' },
            searchFilter: '',
            isSearching: false,
            searchValue: '',

            //filters
            filterTargets: !this.filtersData ? {} : this.filtersData.filterTargets,
            filters: !this.filtersData ? [] : this.filtersData.filters,

            // filters dates
            filterCreatedDate: cloneDeep(get(this.defaultDates, 'created', {})),
            filterStartDate: cloneDeep(get(this.defaultDates, 'start', {})),
            filterEndDate: cloneDeep(get(this.defaultDates, 'end', {})),
            displayTo: {
                filterStartDate: false,
                filterEndDate: false
            },
            pickerOptionsStartOrCreatedTo: {
                disabledDate(time) {
                    return time ? time.getTime() > Date.now() : null
                }
            },
            disabledRowIdsArray: this.disabledRowIds === undefined ? [] : this.disabledRowIds,
            displayFiltersStatus: false,
            partnersFilter: []
        }
    },

    computed: {
        totalItems() {
            return size(this.filteredData)
        },

        // SEARCH
        _searchResults() {
            return this.isSearching ? this.searchResults : this.fullData
        },

        // SORT
        sortedItems() {
            return dataToSortedItems(this.filteredData, this.sorting)
        },

        // PAGINATION
        paginatedData() {
            return dataToPaginateItems(this.sortedItems, { currentPage: this.pagination.currentPage, rowPerPage: this.pagination.currentPageSize })
        },
        paginatedDataIds() {
            return this.paginatedData.map(item => item.id)
        },

        // FILTERS
        filteredData() {
            return !this.isFiltering
                ? this._searchResults
                : dataToFilteredItems(
                    this._searchResults,
                    nth(this.filterDatesKeys, 0),
                    nth(this.filterDatesKeys, 1),
                    {
                        createdDate: this.filterCreatedDate,
                        startDate: this.filterStartDate,
                        endDate: this.filterEndDate
                    },
                    this.filterTargets,
                    nth(this.filterDatesKeys, 2),
                    this.partnersFilter,
                    this.$store.state.data.transfer.list
                )
        },
        isFiltering() {
            if (!this.displayFilters) return false

            const createdDate = get(this.filterCreatedDate, 'from', null)
            const startDate = get(this.filterStartDate, 'from', null)
            const endDate = get(this.filterEndDate, 'from', null)

            return (
                this.filterTargets.bootstrap !== '' ||
                this.filterTargets.status !== '' ||
                this.filterTargets.type !== '' ||
                this.filterTargets.shared !== '' ||
                !isNil(createdDate) ||
                !isNil(startDate) ||
                !isNil(endDate) ||
                !isEmpty(this.partnersFilter)
            )
        },
        searchResults() {
            return dataToSearchedItems(this.fullData, this.searchValue, this.searchFilter)
        }
    },

    methods: {
        debounceRetrieveVolumes: debounce(function() {
            this.audienceActions.retrieveVolumes(this.paginatedData)
        }, 1000),

        // TABLE
        rowClick(rowId) {
            if (this.disabledRowIdsArray.includes(rowId)) {
                return
            }

            //set defaut tab if required
            if (!isNil(this.defaultTab) && isNil(this.selectedRowId)) {
                this.setActiveTab({ tab: this.defaultTab })
            }

            //select on first click, unselect on second click
            const _rowId = rowId !== this.selectedRowId ? rowId : null

            //update selectedRowId in store
            this.selectRow({ rowId: _rowId })
        },
        async loadData(pagination, sorting) {
            if (!isEmpty(sorting)) this.sorting = sorting
            this.pagination = {
                ...pagination,
                itemsPerPageList: ITEMS_PER_PAGE_LIST
            }
        },
        async loadChildData(id) {
            this.setExpandedRows({ id })
        },

        // PAGINATION
        setCurrentPageToTargetSelectedAudience() {
            const selectedAudienceIndex = findIndex(this.sortedItems, item => item.id === this.selectedRowId) + 1
            this.pagination.currentPage = Math.ceil(selectedAudienceIndex / this.pagination.currentPageSize)
            this.audienceActions.updateTargetedCcloneAudience(false)
        },

        // SEARCH
        updateSearchFilter(value) {
            this.searchFilter = value
        },
        launchSearch(searchValue) {
            this.searchValue = searchValue
            this.isSearching = searchValue !== ''
        },

        // FILTERS
        updateFilter(filtering) {
            this.filterTargets[filtering.key] = filtering.value
        },
        resetFilters() {
            this.filterTargets = { bootstrap: '', status: '', type: '', shared: '', type_volume: '' }
            this.filterCreatedDate = cloneDeep(FILTERING_DEFAULT_DATES.created)
            this.filterStartDate = cloneDeep(FILTERING_DEFAULT_DATES.start)
            this.filterEndDate = cloneDeep(FILTERING_DEFAULT_DATES.end)
            this.displayTo = {
                filterCreatedDate: false,
                filterStartDate: false,
                filterEndDate: false
            }
            this.pagination.currentPage = 1
        },

        // DATE FILTERS
        updateDate(key, target, value) {
            this[key][target] = value
            this.$emit('updateDate', key, target, value)
        },

        updateDateOperator(key, value) {
            this[key].operator = value
            if (value === 'period') {
                this.displayTo[key] = true
            } else {
                this.displayTo[key] = false
            }
        },

        //PARTNERS FILTERS
        updatePartners(event) {
            this.$emit('updatePartners', event)
            this.partnersFilter = event
        },

        filtersBlockOpen(value) {
            this.displayFiltersStatus = value
        }
    }
}
</script>
